import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { map, isEmpty } from 'underscore';
import Promise from 'bluebird';
import { ChoiceGroup } from '@fluentui/react/lib/ChoiceGroup';
import classnames from 'classnames';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { FontIcon } from '@fluentui/react/lib/Icon';

import { LocalImageNames, loadLocalImage } from '@bingads-webui-campaign-react/static-local-image-selector';

const imageOptionStyles = {
  root: {
    marginTop: 16,
    marginRight: 13,
  },
  field: {
    paddingBottom: 0,
    paddingTop: 0,
    zIndex: 1,
  },
  innerField: {
    paddingLeft: 0,
    paddingRight: 0,
    zIndex: -1,
  },
  imageWrapper: {
    paddingBottom: 0,
  },
  labelWrapper: {
    display: 'none',
  },
};
const imageSize = { width: 135, height: 76 };

const getRadioButtonStyles = (_, { palette = {} }) => ({
  selectedIcon: {
    position: 'absolute',
    top: 4,
    right: 4,
    backgroundColor: palette.accent,
    width: 20,
    height: 20,
    color: 'white',
    lineHeight: '18px',
    textAlign: 'center',
    borderRadius: '50%',
    zIndex: 2,
    boxSizing: 'border-box',
    border: '1px solid white',
  },
  displayNone: {
    display: 'none',
  },
  icon: {
    fontSize: 12,
    fontWeight: 'bold',
  },
});

export const LocalImagesSelect = ({
  onSelected,
  imageName,
  hidden,
  i18n,
}) => {
  const [images, setImages] = useState([]);
  const classes = useDefaultStyles(getRadioButtonStyles);

  const options = map(images, (imageSrc, index) => ({
    key: LocalImageNames[index],
    ariaLabel: i18n.getString(_TL_('Image {{number}}'), { number: index + 1 }),
    imageSrc,
    selectedImageSrc: imageSrc,
    imageSize,
    styles: imageOptionStyles,
    height: 18,
    width: 18,
    onRenderField: (props, render) => (
      <React.Fragment>
        {render(props)}
        <div
          className={classnames(
            classes.selectedIcon,
            imageName !== LocalImageNames[index] && classes.displayNone
          )}
        >
          <FontIcon aria-label="Accept" iconName="Accept" className={classes.icon} />
        </div>
      </React.Fragment>
    ),
  }));
  const onChange = (event, option) => {
    onSelected(option.key);
  };

  useEffect(() => {
    const imageLoaders = LocalImageNames.map(name => loadLocalImage(name, true /* isMobile */));

    Promise.all(imageLoaders)
      .then((loadedResult) => {
        setImages(loadedResult);
      });
  }, []);

  const invisibleChoiceGroupLabelId = 'invisible-image-select-choice-group-labelId';

  return (
    hidden || isEmpty(images) ? null :
      (
        <React.Fragment>
          <div style={{ display: 'none' }} id={invisibleChoiceGroupLabelId} >
            {i18n.getString(_TL_('Choose one image'))}
          </div>
          <ChoiceGroup
            defaultSelectedKey={imageName}
            options={options}
            onChange={onChange}
            styles
            ariaLabelledBy={invisibleChoiceGroupLabelId}
          />
        </React.Fragment>
      )
  );
};

LocalImagesSelect.propTypes = {
  onSelected: PropTypes.func.isRequired,
  imageName: PropTypes.string,
  hidden: PropTypes.bool.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
};

LocalImagesSelect.defaultProps = {
  imageName: LocalImageNames[0],
};
